/**
 * Dashboard page - Overview and quick actions
 */

import {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {useMeetingsStore} from '@voxapilot/shared';
import {useMeetings} from '../hooks/useMeetings';
import {useRealtimeMeetings} from '../hooks/useRealtime';
import {api} from '../config/firebase';
import {ROUTES} from '../config/constants';
import {Loading} from '../components/ui/Loading';
import {Button} from '../components/ui/Button';
import {EmptyState} from '../components/ui/EmptyState';
import {cn} from '../utils/cn';
import type {DashboardAnalytics} from '@voxapilot/shared';

export function DashboardPage() {
  const {meetings, total, isLoading: storeLoading} = useMeetingsStore();
  const {loadMeetings, isLoading: fetchLoading} = useMeetings();
  const [stats, setStats] = useState({
    total: 0,
    draft: 0,
    completed: 0,
    inProgress: 0,
  });
  const [analytics, setAnalytics] = useState<DashboardAnalytics | null>(null);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);

  // Enable real-time updates
  useRealtimeMeetings();

  // Fetch meetings on mount
  useEffect(() => {
    loadMeetings();
  }, [loadMeetings]);

  // Fetch analytics on mount with timeout
  useEffect(() => {
    const loadAnalytics = async () => {
      try {
        setIsLoadingAnalytics(true);
        setAnalyticsError(null);
        
        // Add timeout to prevent infinite loading
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Analytics request timed out')), 10000);
        });
        
        const analyticsPromise = api.analytics.getAnalytics();
        
        const response = await Promise.race([analyticsPromise, timeoutPromise]) as any;
        
        if (response.success && response.data) {
          setAnalytics(response.data);
        } else {
          throw new Error(response.error?.message || 'Failed to load analytics');
        }
      } catch (error: any) {
        console.error('Failed to load analytics:', error);
        setAnalyticsError(error.message || 'Failed to load analytics');
        // Don't block the UI - analytics is optional
      } finally {
        setIsLoadingAnalytics(false);
      }
    };

    loadAnalytics();
  }, []);

  // Calculate statistics
  useEffect(() => {
    const draft = meetings.filter(m => m.status === 'draft').length;
    const completed = meetings.filter(m => m.status === 'completed').length;
    const inProgress = meetings.filter(m => 
      m.status === 'recording' || 
      m.status === 'processing' ||
      m.transcriptionStatus === 'processing' ||
      m.summarizationStatus === 'processing'
    ).length;

    setStats({
      total: total || meetings.length,
      draft,
      completed,
      inProgress,
    });
  }, [meetings, total]);

  const isLoading = storeLoading || fetchLoading;

  // Get recent meetings (last 5)
  const recentMeetings = meetings.slice(0, 5);


  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Dashboard
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Overview of your meetings and activity
          </p>
        </div>
        <Link to={ROUTES.MEETINGS}>
          <Button>View All Meetings</Button>
        </Link>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <Loading size="lg" />
        </div>
      ) : (
        <>
          {/* Statistics Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              title="Total Meetings"
              value={analytics?.meetings.totalMeetings ?? stats.total}
              icon="📊"
              color="blue"
            />
            <StatCard
              title="Total Duration"
              value={`${Math.round(analytics?.meetings.totalDuration ?? 0)}m`}
              icon="⏱️"
              color="green"
            />
            <StatCard
              title="Avg Duration"
              value={`${Math.round(analytics?.meetings.averageDuration ?? 0)}m`}
              icon="📈"
              color="yellow"
            />
            <StatCard
              title="Completed"
              value={analytics?.meetings.byStatus.completed ?? stats.completed}
              icon="✅"
              color="purple"
            />
          </div>

          {/* Charts */}
          {analytics && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Meeting Trends Chart */}
              {analytics.meetings.overTime.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    Meeting Trends
                  </h2>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={analytics.meetings.overTime}>
                      <CartesianGrid strokeDasharray="3 3" stroke={document.documentElement.classList.contains('dark') ? '#374151' : '#e5e7eb'} />
                      <XAxis
                        dataKey="date"
                        stroke={document.documentElement.classList.contains('dark') ? '#9ca3af' : '#6b7280'}
                        style={{fontSize: '12px'}}
                        tickFormatter={(value: number) => {
                          const date = new Date(value);
                          return `${date.getMonth() + 1}/${date.getDate()}`;
                        }}
                      />
                      <YAxis stroke={document.documentElement.classList.contains('dark') ? '#9ca3af' : '#6b7280'} style={{fontSize: '12px'}} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: document.documentElement.classList.contains('dark') ? '#1f2937' : 'white',
                          border: `1px solid ${document.documentElement.classList.contains('dark') ? '#374151' : '#e5e7eb'}`,
                          borderRadius: '8px',
                          color: document.documentElement.classList.contains('dark') ? '#f3f4f6' : '#111827',
                        }}
                        labelFormatter={(value: string | number) => {
                          const date = new Date(value);
                          return date.toLocaleDateString();
                        }}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="count"
                        stroke="#3b82f6"
                        strokeWidth={2}
                        name="Meetings"
                        dot={{fill: '#3b82f6', r: 4}}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Duration Chart */}
              {analytics.meetings.overTime.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    Meeting Status Distribution
                  </h2>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={[
                      {name: 'Draft', value: analytics.meetings.byStatus.draft},
                      {name: 'Recording', value: analytics.meetings.byStatus.recording},
                      {name: 'Processing', value: analytics.meetings.byStatus.processing},
                      {name: 'Completed', value: analytics.meetings.byStatus.completed},
                      {name: 'Archived', value: analytics.meetings.byStatus.archived},
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" stroke={document.documentElement.classList.contains('dark') ? '#374151' : '#e5e7eb'} />
                      <XAxis dataKey="name" stroke={document.documentElement.classList.contains('dark') ? '#9ca3af' : '#6b7280'} style={{fontSize: '12px'}} />
                      <YAxis stroke={document.documentElement.classList.contains('dark') ? '#9ca3af' : '#6b7280'} style={{fontSize: '12px'}} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: document.documentElement.classList.contains('dark') ? '#1f2937' : 'white',
                          border: `1px solid ${document.documentElement.classList.contains('dark') ? '#374151' : '#e5e7eb'}`,
                          borderRadius: '8px',
                          color: document.documentElement.classList.contains('dark') ? '#f3f4f6' : '#111827',
                        }}
                      />
                      <Bar dataKey="value" fill="#3b82f6" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}

          {/* Analytics Loading State */}
          {isLoadingAnalytics && !analytics && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12">
              <div className="flex justify-center items-center">
                <Loading size="lg" />
              </div>
            </div>
          )}

          {/* Analytics Error State */}
          {analyticsError && !analytics && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <EmptyState
                icon="📊"
                title="Unable to load analytics"
                description={analyticsError}
                size="sm"
              />
            </div>
          )}

          {/* Insights Section */}
          {analytics && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Key Insights
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <InsightCard
                  icon="📅"
                  title="Most Active Day"
                  value={getMostActiveDay(analytics.meetings.overTime)}
                  description="Day with most meetings"
                />
                <InsightCard
                  icon="⏱️"
                  title="Total Hours"
                  value={`${Math.round(analytics.meetings.totalDuration / 60)}h`}
                  description="Total meeting time recorded"
                />
                <InsightCard
                  icon="📈"
                  title="Avg Meeting Length"
                  value={`${Math.round(analytics.meetings.averageDuration)}m`}
                  description="Average duration per meeting"
                />
                <InsightCard
                  icon="🎯"
                  title="Completion Rate"
                  value={`${getCompletionRate(analytics.meetings)}%`}
                  description="Percentage of completed meetings"
                />
                <InsightCard
                  icon="📊"
                  title="Total Recordings"
                  value={analytics.usage.totalRecordings.toString()}
                  description="Meetings with audio recordings"
                />
                <InsightCard
                  icon="📝"
                  title="Transcriptions"
                  value={analytics.usage.totalTranscriptions.toString()}
                  description="Completed transcriptions"
                />
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <QuickActionCard
              title="New Meeting"
              description="Create a new meeting"
              icon="➕"
              link={ROUTES.MEETINGS}
              action="Create"
            />
            <QuickActionCard
              title="View All Meetings"
              description="Browse all your meetings"
              icon="📋"
              link={ROUTES.MEETINGS}
              action="View"
            />
            <QuickActionCard
              title="Settings"
              description="Manage your preferences"
              icon="⚙️"
              link={ROUTES.SETTINGS}
              action="Open"
            />
          </div>

          {/* Recent Meetings */}
          {recentMeetings.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Recent Meetings
                </h2>
                <Link
                  to={ROUTES.MEETINGS}
                  className="text-orange-600 dark:text-orange-400 hover:underline text-sm"
                >
                  View all
                </Link>
              </div>
              <div className="space-y-3">
                {recentMeetings.map((meeting) => (
                  <Link
                    key={meeting.id}
                    to={ROUTES.MEETING_DETAIL(meeting.id!)}
                    className="block p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900 dark:text-white">
                          {meeting.title}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {meeting.createdAt
                            ? new Date(meeting.createdAt).toLocaleDateString()
                            : 'No date'}
                        </p>
                      </div>
                      <StatusBadge status={meeting.status} />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Draft Meetings */}
          {meetings.filter(m => m.status === 'draft' && m.scheduledDate).length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Scheduled Meetings
              </h2>
              <div className="space-y-3">
                {meetings
                  .filter(m => m.status === 'draft' && m.scheduledDate)
                  .sort((a, b) => {
                    const dateA = new Date(a.scheduledDate || 0).getTime();
                    const dateB = new Date(b.scheduledDate || 0).getTime();
                    return dateA - dateB;
                  })
                  .slice(0, 5)
                  .map((meeting) => (
                    <Link
                      key={meeting.id}
                      to={ROUTES.MEETING_DETAIL(meeting.id!)}
                      className="block p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900 dark:text-white">
                            {meeting.title}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {meeting.scheduledDate
                              ? new Date(meeting.scheduledDate).toLocaleString()
                              : 'No date'}
                          </p>
                        </div>
                        <StatusBadge status={meeting.status} />
                      </div>
                    </Link>
                  ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {meetings.length === 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
              <div className="text-6xl mb-4">📅</div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                No meetings yet
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Get started by creating your first meeting
              </p>
              <Link to={ROUTES.MEETINGS}>
                <Button>Create Meeting</Button>
              </Link>
            </div>
          )}
        </>
      )}
    </div>
  );
}

interface StatCardProps {
  title: string;
  value: number | string;
  icon: string;
  color: 'blue' | 'green' | 'yellow' | 'purple';
}

function StatCard({title, value, icon, color}: StatCardProps) {
  const colorClasses = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
    green: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
    yellow: 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400',
    purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
            {title}
          </p>
          <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
            {value}
          </p>
        </div>
        <div className={cn('p-3 rounded-lg text-2xl', colorClasses[color])}>
          {icon}
        </div>
      </div>
    </div>
  );
}

interface QuickActionCardProps {
  title: string;
  description: string;
  icon: string;
  link: string;
  action: string;
}

function QuickActionCard({title, description, icon, link, action}: QuickActionCardProps) {
  return (
    <Link
      to={link}
      className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 hover:shadow-lg transition-shadow block"
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="text-3xl mb-3">{icon}</div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
            {title}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {description}
          </p>
        </div>
        <Button variant="outline" size="sm">
          {action}
        </Button>
      </div>
    </Link>
  );
}

interface StatusBadgeProps {
  status: string;
}

function StatusBadge({status}: StatusBadgeProps) {
  const statusColors: Record<string, string> = {
    draft: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200',
    recording: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200',
    processing: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200',
    completed: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200',
    archived: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
  };

  return (
    <span
      className={cn(
        'px-2 py-1 text-xs font-medium rounded-full',
        statusColors[status] || statusColors.cancelled
      )}
    >
      {status}
    </span>
  );
}

interface InsightCardProps {
  icon: string;
  title: string;
  value: string;
  description: string;
}

function InsightCard({icon, title, value, description}: InsightCardProps) {
  return (
    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
      <div className="flex items-start gap-3">
        <div className="text-2xl">{icon}</div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
            {title}
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
            {value}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-500">
            {description}
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Get the most active day of the week from meeting data
 */
function getMostActiveDay(overTime: Array<{date: string; count: number}>): string {
  if (!overTime || overTime.length === 0) {
    return 'N/A';
  }

  const dayCounts: Record<string, number> = {};
  
  overTime.forEach(({date, count}) => {
    const dayOfWeek = new Date(date).toLocaleDateString('en-US', {weekday: 'long'});
    dayCounts[dayOfWeek] = (dayCounts[dayOfWeek] || 0) + count;
  });

  const mostActiveDay = Object.entries(dayCounts).reduce((max, [day, count]) => {
    return count > max.count ? {day, count} : max;
  }, {day: 'Monday', count: 0});

  return mostActiveDay.count > 0 ? mostActiveDay.day : 'N/A';
}

/**
 * Calculate completion rate percentage
 */
function getCompletionRate(meetings: {
  totalMeetings: number;
  byStatus: {
    completed: number;
  };
}): number {
  if (meetings.totalMeetings === 0) {
    return 0;
  }
  return Math.round((meetings.byStatus.completed / meetings.totalMeetings) * 100);
}
